import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { BookingBand } from "@/components/site/BookingBand";
import { Marquee } from "@/components/site/Marquee";
import { Sparkle, SparkleDivider } from "@/components/site/Sparkle";
import { IMG } from "@/data/site";

const CHAPTERS = [
  {
    num: "01",
    title: "Precision Craft",
    body: "A great cut is geometry you can wear. We study bone structure, growth patterns, and how your hair behaves on an ordinary Tuesday — not just how it looks leaving the chair. The result is work that holds its shape for weeks, not hours.",
    image: IMG.tools,
  },
  {
    num: "02",
    title: "A Hushed Sanctuary",
    body: "No ringing phones at the front desk, no row of waiting clients, no rush. One guest at a time, in a private suite with soft light and softer edges. The calm is not an accident — it is the design.",
    image: IMG.interior1,
  },
  {
    num: "03",
    title: "Medical Integrity",
    body: "Our laser and skin work is performed with medical-grade equipment, honest assessments, and patch tests before promises. If a treatment is not right for your skin, we will tell you — and tell you what is.",
    image: IMG.laser2,
  },
];

const SPECIALISTS = [
  { initial: "S", role: "Master Stylist & Colour Director", bio: "Precision cutting and lived-in colour, refined over years behind the chair." },
  { initial: "Z", role: "Clinical Laser & Skin Specialist", bio: "Medical-grade laser and advanced skin protocols, delivered with a gentle hand." },
];

export default function About() {
  return (
    <>
      <PageHero
        testId="about-hero"
        eyebrow="Our Story"
        titleLines={["An Atelier,", "Not a Salon"]}
        quote="Built slowly, on purpose, in the heart of Brampton."
        image={IMG.spa2}
        imageAlt="Soft light across a calm treatment suite at the S & Z studio"
      />

      <section data-testid="about-intro" className="bg-ivory py-24 sm:py-36">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <SparkleDivider />
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-quote italic font-light text-2xl sm:text-3xl lg:text-4xl leading-[1.4] text-deep mt-10">
              "S &amp; Z began with a simple conviction: that beauty work, done
              properly, should feel like a private ritual — unhurried, exact,
              and entirely yours."
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-chapters" className="bg-ivory pb-24 sm:pb-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal className="mb-16 sm:mb-24">
            <Eyebrow>The Manifesto</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-espresso mt-4">
              Three Chapters of <span className="italic text-gold">One Belief</span>
            </h2>
          </Reveal>
          <div className="flex flex-col space-y-24 lg:space-y-36">
            {CHAPTERS.map((c, i) => (
              <div
                key={c.num}
                data-testid={`manifesto-chapter-${c.num}`}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center`}
              >
                <Reveal className={`lg:col-span-6 ${i % 2 ? "lg:order-2" : ""}`}>
                  <div className="overflow-hidden img-frame border border-stone group">
                    <img
                      src={c.image}
                      alt={c.title}
                      loading="lazy"
                      className="w-full h-[320px] sm:h-[440px] object-cover transition-transform duration-700 hover:scale-[1.03]"
                    />
                  </div>
                </Reveal>
                <div className={`lg:col-span-6 ${i % 2 ? "lg:order-1 lg:text-right" : ""}`}>
                  <Reveal>
                    <span className="font-display text-6xl sm:text-7xl text-champagne/70">
                      {c.num}
                    </span>
                  </Reveal>
                  <Reveal delay={0.1}>
                    <h3 className="font-display text-3xl sm:text-4xl text-espresso mt-2">
                      {c.title}
                    </h3>
                  </Reveal>
                  <Reveal delay={0.2}>
                    <p className="text-deep/80 text-base sm:text-lg leading-relaxed mt-5 max-w-lg lg:inline-block">
                      {c.body}
                    </p>
                  </Reveal>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Marquee dark />

      <section data-testid="about-specialists" className="bg-ivory py-24 sm:py-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal className="text-center mb-16">
            <Eyebrow>
              <span className="mx-auto">The Hands Behind the Name</span>
            </Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl text-espresso mt-4">
              Your <span className="italic text-gold">Specialists</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 max-w-4xl mx-auto">
            {SPECIALISTS.map((s, i) => (
              <Reveal key={s.initial} delay={0.12 * i}>
                <div
                  data-testid={`specialist-card-${i}`}
                  className="border border-stone hover:border-gold transition-colors duration-500 bg-ivory p-10 text-center img-frame h-full"
                >
                  <div className="w-20 h-20 mx-auto rounded-full border border-gold flex items-center justify-center">
                    <span className="font-display text-4xl text-gold">{s.initial}</span>
                  </div>
                  <h3 className="font-display text-2xl text-espresso mt-6">{s.role}</h3>
                  <p className="text-deep/70 text-sm mt-4 leading-relaxed">{s.bio}</p>
                  <div className="flex justify-center mt-6">
                    <Sparkle size={10} className="opacity-60" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <BookingBand />
    </>
  );
}
